import { useState } from "react";
import { collectionService } from "@/services";
import type { CollectionDemand, PaymentMode } from "@/services/types";
import { useAuth } from "@/context/AuthContext";
import { useToast } from "@/context/ToastContext";
import { Button } from "@/components/base/Button";
import { CurrencyInput } from "@/components/currency/CurrencyInput";
import { CurrencyDisplay } from "@/components/currency/CurrencyDisplay";
import { formatINRNumber } from "@/utils/format";
import { PaymentForm } from "../../components/PaymentForm";
import { PreviousDuesAlertCard } from "../../components/PreviousDuesAlertCard";

interface InlinePayCardProps {
  demand: CollectionDemand;
  onDone: () => void;
}

export function InlinePayCard({ demand, onDone }: InlinePayCardProps) {
  const { user } = useAuth();
  const toast = useToast();
  const actor = user?.name ?? "Staff User";

  // Base due (excluding any late fee):
  const baseDue = Math.max(0, demand.balance - Math.max(0, demand.lateFee || 0));
  
  // Late fee amount (either already applied or applicable/overdue):
  const lateFeeAmount = Math.max(0, demand.lateFee > 0 ? demand.lateFee : (demand.applicableLateFee ?? 0));
  const hasLateFee = lateFeeAmount > 0;

  // Option for user: apply late fee or not! Default is false (user selects if required)
  const [applyLateFee, setApplyLateFee] = useState<boolean>(false);

  // Total payable dynamically depends on whether late fee is selected:
  const currentPayable = hasLateFee && applyLateFee ? baseDue + lateFeeAmount : baseDue;

  const [amount, setAmount] = useState<number | null>(baseDue);

  const handleToggleLateFee = (checked: boolean) => {
    setApplyLateFee(checked);
    const newPayable = checked && hasLateFee ? baseDue + lateFeeAmount : baseDue;
    setAmount(newPayable);
  };

  const submit = async (values: { mode: PaymentMode; reference: string; receivedOn: string; remarks: string }) => {
    if (!amount || amount <= 0) {
      toast.error("Enter an amount", "The payment amount must be greater than zero.");
      return false;
    }
    if (amount > currentPayable + 0.01) {
      toast.error(
        "Amount too high",
        `The outstanding balance ${hasLateFee && applyLateFee ? "(including late fee) " : ""}is ${formatINRNumber(currentPayable)}.`
      );
      return false;
    }
    try {
      const { receipts } = await collectionService.recordPayment({
        memberId: demand.memberId,
        payments: [{ demandId: demand.id, amount, groupMemberId: demand.groupMemberId }],
        mode: values.mode,
        reference: values.reference,
        receivedOn: values.receivedOn,
        remarks: values.remarks,
        actor,
        applyLateFee: hasLateFee ? applyLateFee : false,
      });
      const receipt = receipts[0];
      toast.success("Payment recorded", `${receipt.receiptNumber} issued for ${formatINRNumber(receipt.total)}.`);
      onDone();
      return true;
    } catch (err) {
      toast.error("Could not record payment", err instanceof Error ? err.message : "Please try again.");
      return false;
    }
  };

  return (
    <div className="rounded-lg border border-primary-200 bg-primary-50/40 p-4 space-y-3">
      {/* Alert card for previous pending dues in this group */}
      {demand.previousPendingDues && demand.previousPendingDues.length > 0 ? (
        <PreviousDuesAlertCard
          memberName={demand.memberName}
          groupName={demand.groupName}
          previousDues={demand.previousPendingDues}
          totalDues={demand.totalPreviousPendingDues}
        />
      ) : null}

      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div className="flex h-9 w-9 items-center justify-center rounded-full bg-primary-100 text-primary-700">
            <i className="ri-user-star-line text-lg" />
          </div>
          <div>
            <p className="text-sm font-semibold text-foreground-900">{demand.memberName}</p>
            <p className="tnum text-xs text-foreground-500">
              {demand.ticketNumber} · {demand.memberPhone}
            </p>
          </div>
        </div>
        <div className="text-right">
          <p className="text-xs text-foreground-500">Outstanding payable</p>
          <p className="tnum text-lg font-semibold text-foreground-950">
            <CurrencyDisplay amount={currentPayable} hideSymbol />
          </p>
          {hasLateFee ? (
            applyLateFee ? (
              <p className="text-[11px] font-medium text-amber-600">
                Includes {formatINRNumber(lateFeeAmount)} late fee
              </p>
            ) : (
              <p className="text-[11px] font-medium text-success-600">
                Late fee not applied (₹0.00 fee)
              </p>
            )
          ) : null}
        </div>
      </div>

      {hasLateFee && (
        <div className="mb-3 rounded-lg border border-amber-200 bg-amber-50/80 p-3">
          <div className="flex items-center justify-between gap-3">
            <div className="flex items-start gap-2.5">
              <div className="mt-0.5 flex h-7 w-7 flex-none items-center justify-center rounded-full bg-amber-100 text-amber-700">
                <i className="ri-timer-flash-line text-base" />
              </div>
              <div>
                <div className="flex items-center gap-2">
                  <span className="text-xs font-semibold uppercase tracking-wider text-amber-900">
                    Late fee: {formatINRNumber(lateFeeAmount)}
                  </span>
                  <span
                    className={`inline-flex items-center text-[10px] font-semibold px-1.5 py-0.5 rounded ${
                      applyLateFee ? "bg-amber-200/70 text-amber-900" : "bg-foreground-100 text-foreground-600"
                    }`}
                  >
                    {applyLateFee ? "APPLIED IN DUE" : "NOT APPLIED"}
                  </span>
                </div>
                <p className="mt-0.5 text-xs text-amber-800">
                  {applyLateFee
                    ? `Late fee of ${formatINRNumber(lateFeeAmount)} will apply in installment due.`
                    : `Late fee is excluded. Only base installment of ${formatINRNumber(baseDue)} will apply.`}
                </p>
              </div>
            </div>
            <label className="flex flex-none items-center gap-2 cursor-pointer select-none rounded-md bg-white px-2.5 py-1.5 border border-amber-200 shadow-sm hover:bg-amber-50/50">
              <input
                type="checkbox"
                checked={applyLateFee}
                onChange={(e) => handleToggleLateFee(e.target.checked)}
                className="h-4 w-4 rounded border-amber-300 text-primary-600 focus:ring-primary-500 cursor-pointer"
              />
              <span className="text-xs font-medium text-foreground-800">
                Apply late fee
              </span>
            </label>
          </div>
        </div>
      )}

      <div className="mb-3">
        <CurrencyInput label="Amount received" value={amount} onChange={setAmount} />
        <button
          type="button"
          onClick={() => setAmount(currentPayable)}
          className="mt-1.5 inline-flex cursor-pointer items-center gap-1 text-xs font-medium text-primary-600 hover:underline"
        >
          <i className="ri-arrow-down-line" /> Full amount ({formatINRNumber(currentPayable)})
        </button>
      </div>
      <PaymentForm
        onSubmit={submit}
        submitLabel="Record & issue receipt"
        submitIcon="ri-receipt-line"
        autoFocus
        availableBalance={demand.accountBalance}
        amountToPay={amount ?? 0}
        defaultMode={demand.accountBalance && demand.accountBalance >= (amount ?? 0) ? "member_account" : "cash"}
        amountSummary={
          <div className="space-y-0.5">
            <span className="text-sm text-foreground-600">
              Paying <span className="tnum font-semibold text-foreground-900">{amount ? formatINRNumber(amount) : "0.00"}</span> toward{" "}
              {demand.groupName} cycle {demand.cycle}
            </span>
            {hasLateFee ? (
              applyLateFee ? (
                <p className="text-xs text-amber-600">
                  Base installment is {formatINRNumber(baseDue)} + {formatINRNumber(lateFeeAmount)} late fee.
                </p>
              ) : (
                <p className="text-xs text-success-600">
                  Late fee not applied. Paying toward base due of {formatINRNumber(baseDue)}.
                </p>
              )
            ) : null}
          </div>
        }
      />
      <div className="mt-2 flex justify-end">
        <Button size="sm" variant="ghost" onClick={onDone}>
          Cancel
        </Button>
      </div>
    </div>
  );
}